import React, { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Shield, Users, Crown, MessageCircle, Calendar, Heart,
  TrendingUp, DollarSign, Search, LogOut, ChevronRight, ChevronLeft,
  Baby, FileText, Mail, MapPin, Clock, AlertCircle, Plus,
  Bug, Send, Newspaper, Landmark, Target, ExternalLink, Trash2,
  Receipt, Building, CheckCircle, Star, Eye, EyeOff, Image, Upload, Copy, Loader2
} from "lucide-react";
import AdminContentManager from "@/components/AdminContentManager";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Grant status colors
const STATUS_COLORS = {
  researching: "bg-slate-100 text-slate-700",
  preparing: "bg-blue-100 text-blue-700",
  submitted: "bg-amber-100 text-amber-700",
  approved: "bg-green-100 text-green-700",
  rejected: "bg-red-100 text-red-700",
  not_applicable: "bg-slate-100 text-purple-500"
};

// Invoice status colors
const INVOICE_STATUS_COLORS = {
  draft: "bg-slate-100 text-slate-700",
  sent: "bg-blue-100 text-blue-700",
  paid: "bg-green-100 text-green-700",
  overdue: "bg-red-100 text-red-700"
};

const AdminDashboard = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);
  const [userDetail, setUserDetail] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [activeTab, setActiveTab] = useState("overview");
  const [outreach, setOutreach] = useState(null);
  const [notes, setNotes] = useState([]);
  const [newNote, setNewNote] = useState("");
  const [debugMode, setDebugMode] = useState(false);
  const [newsletter, setNewsletter] = useState({ subject: "", content: "", audience: "all" });
  const [subscribers, setSubscribers] = useState({ total_active: 0, subscribers: [] });
  const [grants, setGrants] = useState({ grants: [], monthly_history: [] });
  const [goals, setGoals] = useState([]);
  const [seedGrants, setSeedGrants] = useState([]);
  const [showAddGrant, setShowAddGrant] = useState(false);
  const [newGrant, setNewGrant] = useState({ name: "", funder: "", description: "", typical_amount: "", url: "", status: "researching", priority: "medium" });
  const [invoices, setInvoices] = useState({ invoices: [], stats: {} });
  const [showCreateInvoice, setShowCreateInvoice] = useState(false);
  const [newInvoice, setNewInvoice] = useState({ partner_name: "", partner_email: "", description: "", amount_pounds: "", due_days: 30, notes: "" });
  const [previewAsFree, setPreviewAsFree] = useState(false);
  
  // Media Library state
  const [mediaFiles, setMediaFiles] = useState([]);
  const [mediaUploading, setMediaUploading] = useState(false);
  const [mediaCategory, setMediaCategory] = useState("general");
  const [mediaAltText, setMediaAltText] = useState("");

  const adminToken = localStorage.getItem("admin_token");

  useEffect(() => {
    if (!adminToken) {
      navigate("/admin");
      return;
    }
    verifyAndFetch();
  }, []);

  const verifyAndFetch = async () => {
    try {
      await axios.get(`${API}/admin/verify`, {
        headers: { Authorization: `Bearer ${adminToken}` }
      });
      fetchDashboard();
      fetchUsers();
    } catch (error) {
      toast.error("Session expired");
      localStorage.removeItem("admin_token");
      navigate("/admin");
    }
  };

  const fetchDashboard = async () => {
    try {
      const res = await axios.get(`${API}/admin/dashboard`, {
        headers: { Authorization: `Bearer ${adminToken}` }
      });
      setStats(res.data);
    } catch (error) {
      console.error("Dashboard error:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchUsers = async (search = "") => {
    try {
      const res = await axios.get(`${API}/admin/users`, {
        headers: { Authorization: `Bearer ${adminToken}` },
        params: { search, limit: 100 }
      });
      setUsers(res.data.users || []);
    } catch (error) {
      console.error("Users error:", error);
    }
  };

  const fetchUserDetail = async (userId) => {
    try {
      const res = await axios.get(`${API}/admin/users/${userId}`, {
        headers: { Authorization: `Bearer ${adminToken}` }
      });
      setUserDetail(res.data);
      setSelectedUser(userId);
      fetchNotes(userId);
      fetchDebugMode(userId);
    } catch (error) {
      toast.error("Failed to load user details");
    }
  };

  const fetchDebugMode = async (userId) => {
    try {
      const res = await axios.get(`${API}/admin/users/${userId}/debug-mode`, {
        headers: { Authorization: `Bearer ${adminToken}` }
      });
      setDebugMode(res.data.debug_mode);
    } catch (error) {
      console.error("Debug mode fetch error:", error);
    }
  };

  const toggleDebugMode = async () => {
    if (!selectedUser) return;
    try {
      await axios.post(`${API}/admin/users/${selectedUser}/debug-mode`,
        { enabled: !debugMode },
        { headers: { Authorization: `Bearer ${adminToken}` } }
      );
      setDebugMode(!debugMode);
      toast.success(`Debug mode ${!debugMode ? 'enabled' : 'disabled'}`);
    } catch (error) {
      toast.error("Failed to toggle debug mode");
    }
  };

  const fetchSubscribers = async () => {
    try {
      const res = await axios.get(`${API}/admin/newsletter/subscribers`, {
        headers: { Authorization: `Bearer ${adminToken}` }
      });
      setSubscribers(res.data);
    } catch (error) {
      console.error("Subscribers fetch error:", error);
    }
  };

  const sendTestEmail = async () => {
    try {
      await axios.post(`${API}/admin/email/test`, {}, {
        headers: { Authorization: `Bearer ${adminToken}` }
      });
      toast.success("Test email sent to team@kiddiplayclub.com");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to send test email");
    }
  };

  const sendNewsletter = async () => {
    if (!newsletter.subject || !newsletter.content) {
      toast.error("Please fill in subject and content");
      return;
    }
    try {
      const res = await axios.post(`${API}/admin/newsletter/send`, {
        subject: newsletter.subject,
        html_content: newsletter.content,
        send_to: newsletter.audience
      }, {
        headers: { Authorization: `Bearer ${adminToken}` }
      });
      toast.success(`Newsletter sent to ${res.data.sent} recipients`);
      setNewsletter({ subject: "", content: "", audience: "all" });
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to send newsletter");
    }
  };

  const fetchGrants = async () => {
    try {
      const [grantsRes, goalsRes, seedRes] = await Promise.all([
        axios.get(`${API}/admin/grants`, { headers: { Authorization: `Bearer ${adminToken}` } }),
        axios.get(`${API}/admin/grants/goals`, { headers: { Authorization: `Bearer ${adminToken}` } }),
        axios.get(`${API}/admin/grants/seed-list`, { headers: { Authorization: `Bearer ${adminToken}` } })
      ]);
      setGrants(grantsRes.data);
      setGoals(goalsRes.data.goals || []);
      setSeedGrants(seedRes.data.grants || []);
    } catch (error) {
      console.error("Grants fetch error:", error);
    }
  };

  const addGrant = async () => {
    if (!newGrant.name) {
      toast.error("Please enter grant name");
      return;
    }
    try {
      await axios.post(`${API}/admin/grants`, newGrant, {
        headers: { Authorization: `Bearer ${adminToken}` }
      });
      toast.success("Grant added to tracker");
      setNewGrant({ name: "", funder: "", description: "", typical_amount: "", url: "", status: "researching", priority: "medium" });
      setShowAddGrant(false);
      fetchGrants();
    } catch (error) {
      toast.error("Failed to add grant");
    }
  };

  const updateGrantStatus = async (grantId, newStatus) => {
    try {
      await axios.put(`${API}/admin/grants/${grantId}`, { status: newStatus }, {
        headers: { Authorization: `Bearer ${adminToken}` }
      });
      toast.success("Status updated");
      fetchGrants();
    } catch (error) {
      toast.error("Failed to update");
    }
  };

  const deleteGrant = async (grantId) => {
    if (!window.confirm("Remove this grant from tracker?")) return;
    try {
      await axios.delete(`${API}/admin/grants/${grantId}`, {
        headers: { Authorization: `Bearer ${adminToken}` }
      });
      toast.success("Grant removed");
      fetchGrants();
    } catch (error) {
      toast.error("Failed to remove");
    }
  };

  const addFromSeed = (seedGrant) => {
    setNewGrant({
      name: seedGrant.name,
      funder: seedGrant.funder,
      description: seedGrant.description,
      typical_amount: seedGrant.typical_amount,
      url: seedGrant.url,
      status: "researching",
      priority: "medium"
    });
    setShowAddGrant(true);
  };

  const fetchOutreach = async () => {
    try {
      const res = await axios.get(`${API}/admin/outreach`, {
        headers: { Authorization: `Bearer ${adminToken}` }
      });
      setOutreach(res.data);
    } catch (error) {
      console.error("Outreach error:", error);
    }
  };

  const fetchNotes = async (userId) => {
    try {
      const res = await axios.get(`${API}/admin/notes/${userId}`, {
        headers: { Authorization: `Bearer ${adminToken}` }
      });
      setNotes(res.data.notes || []);
    } catch (error) {
      console.error("Notes error:", error);
    }
  };

  const addNote = async () => {
    if (!newNote.trim() || !selectedUser) return;
    try {
      await axios.post(`${API}/admin/notes/${selectedUser}`, 
        { note: newNote },
        { headers: { Authorization: `Bearer ${adminToken}` } }
      );
      toast.success("Note added");
      setNewNote("");
      fetchNotes(selectedUser);
    } catch (error) {
      toast.error("Failed to add note");
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    fetchUsers(searchQuery);
  };

  const handleLogout = () => {
    localStorage.removeItem("admin_token");
    navigate("/admin");
  };

  // Media Library Functions
  const fetchMedia = async (category = null) => {
    try {
      const params = category ? `?category=${category}` : "";
      const res = await axios.get(`${API}/admin/media${params}`, {
        headers: { Authorization: `Bearer ${adminToken}` }
      });
      setMediaFiles(res.data.media || []);
    } catch (error) {
      console.error("Media fetch error:", error);
    }
  };

  const handleMediaUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    const allowedTypes = ["image/jpeg", "image/png", "image/gif", "image/webp", "application/pdf"];
    if (!allowedTypes.includes(file.type)) {
      toast.error("Only images (JPG, PNG, GIF, WebP) and PDFs are allowed");
      return;
    }

    // Validate file size (10MB max)
    if (file.size > 10 * 1024 * 1024) {
      toast.error("File too large. Maximum 10MB.");
      return;
    }

    setMediaUploading(true);
    const formData = new FormData();
    formData.append("file", file);
    formData.append("category", mediaCategory);
    formData.append("alt_text", mediaAltText);

    try {
      await axios.post(`${API}/admin/media/upload?category=${mediaCategory}&alt_text=${encodeURIComponent(mediaAltText)}`, formData, {
        headers: { 
          Authorization: `Bearer ${adminToken}`,
          "Content-Type": "multipart/form-data"
        }
      });
      toast.success("File uploaded successfully!");
      setMediaAltText("");
      fetchMedia();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Upload failed");
    } finally {
      setMediaUploading(false);
      // Reset file input
      e.target.value = "";
    }
  };

  const deleteMedia = async (mediaId) => {
    if (!window.confirm("Delete this file?")) return;
    try {
      await axios.delete(`${API}/admin/media/${mediaId}`, {
        headers: { Authorization: `Bearer ${adminToken}` }
      });
      toast.success("File deleted");
      fetchMedia();
    } catch (error) {
      toast.error("Failed to delete");
    }
  };

  const copyMediaUrl = (mediaId) => {
    const url = `${BACKEND_URL}/api/media/${mediaId}`;
    navigator.clipboard.writeText(url);
    toast.success("URL copied to clipboard!");
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "N/A";
    return new Date(dateStr).toLocaleDateString('en-GB', {
      day: 'numeric', month: 'short', year: 'numeric'
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-50 via-pink-50 to-white flex items-center justify-center">
        <div className="w-12 h-12 border-4 border-purple-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 via-pink-50 to-white">
      {/* Header */}
      <header className="bg-gradient-to-r from-purple-600 via-pink-500 to-orange-400 border-b border-purple-200 sticky top-0 z-50 shadow-lg">
        <div className="max-w-7xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            {/* Back to Site Button */}
            <Link to="/">
              <Button
                variant="outline"
                className="bg-white/20 backdrop-blur border-white/30 text-purple-900 hover:bg-white/30"
                data-testid="back-to-site-btn"
              >
                <ChevronLeft className="w-4 h-4 mr-1" />
                Back to Site
              </Button>
            </Link>
            <div className="w-10 h-10 bg-white/20 backdrop-blur rounded-xl flex items-center justify-center">
              <Shield className="w-5 h-5 text-purple-900" />
            </div>
            <div>
              <h1 className="text-lg font-bold text-purple-900">KiddiPlay Admin</h1>
              <p className="text-xs text-purple-900/70">Management Console</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            {/* Preview as Free User Toggle */}
            <Link 
              to={previewAsFree ? "/dashboard" : "/dashboard?preview=free"}
              onClick={() => {
                setPreviewAsFree(!previewAsFree);
                // Set a flag in localStorage for the dashboard to read
                if (!previewAsFree) {
                  localStorage.setItem("admin_preview_free", "true");
                  toast.info("Opening dashboard in Free User preview mode...");
                } else {
                  localStorage.removeItem("admin_preview_free");
                }
              }}
              target="_blank"
            >
              <Button
                variant="outline"
                className="bg-white/20 backdrop-blur border-white/30 text-purple-900 hover:bg-white/30"
                data-testid="preview-free-btn"
              >
                {previewAsFree ? <EyeOff className="w-4 h-4 mr-2" /> : <Eye className="w-4 h-4 mr-2" />}
                Preview as Free User
              </Button>
            </Link>
            <Button
              onClick={handleLogout}
              variant="ghost"
              className="text-purple-900/80 hover:text-purple-900 hover:bg-white/20"
            >
              <LogOut className="w-4 h-4 mr-2" />
              Logout
            </Button>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 py-6">
        {/* Tabs */}
        <div className="flex gap-2 mb-6 overflow-x-auto pb-2">
          {[
            { id: "overview", label: "Overview", icon: TrendingUp },
            { id: "users", label: "Users / CRM", icon: Users },
            { id: "outreach", label: "Outreach", icon: Mail },
            { id: "newsletter", label: "Newsletter", icon: Newspaper },
            { id: "media", label: "Media Library", icon: Image },
            { id: "funding", label: "Funding & Grants", icon: Landmark },
            { id: "content", label: "Content", icon: Star },
            { id: "research", label: "Research", icon: FileText },
            { id: "projections", label: "Projections", icon: DollarSign },
          ].map(tab => (
            <button
              key={tab.id}
              onClick={() => {
                setActiveTab(tab.id);
                if (tab.id === "outreach") fetchOutreach();
                if (tab.id === "newsletter") fetchSubscribers();
                if (tab.id === "funding") fetchGrants();
                if (tab.id === "media") fetchMedia();
              }}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-colors whitespace-nowrap ${
                activeTab === tab.id
                  ? 'bg-gradient-to-r from-purple-500 to-pink-500 text-purple-900'
                  : 'bg-white/80 text-purple-700 hover:bg-purple-100 border border-purple-200'
              }`}
              data-testid={`tab-${tab.id}`}
            >
              <tab.icon className="w-4 h-4" />
              {tab.label}
            </button>
          ))}
        </div>

        {/* Overview Tab */}
        {activeTab === "overview" && stats && (
          <div className="space-y-6">
            {/* Stats Grid */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <StatCard icon={Users} label="Total Users" value={stats.overview.total_users} color="teal" />
              <StatCard icon={Crown} label="Premium Users" value={stats.overview.premium_users} color="amber" />
              <StatCard icon={Heart} label="Matches" value={stats.overview.total_matches} color="pink" />
              <StatCard icon={MessageCircle} label="Messages" value={stats.overview.total_messages} color="blue" />
              <StatCard icon={Calendar} label="Playdates" value={stats.overview.total_playdates} color="purple" />
              <StatCard icon={Baby} label="Sitter Pools" value={stats.overview.total_pools} color="coral" />
              <StatCard icon={FileText} label="Pool Bookings" value={stats.overview.total_pool_bookings} color="green" />
              <StatCard icon={TrendingUp} label="New (7 days)" value={stats.overview.recent_signups} color="cyan" />
            </div>

            {/* Revenue Card */}
            <div className="bg-white rounded-xl p-6 border border-purple-200">
              <h3 className="text-lg font-semibold text-purple-900 mb-4 flex items-center gap-2">
                <DollarSign className="w-5 h-5 text-green-500" />
                Revenue Overview
              </h3>
              <div className="grid grid-cols-3 gap-4">
                <div className="bg-purple-50 rounded-lg p-4">
                  <p className="text-purple-600 text-sm">Subscriptions</p>
                  <p className="text-2xl font-bold text-purple-900">£{stats.revenue.subscription_revenue.toFixed(2)}</p>
                </div>
                <div className="bg-purple-50 rounded-lg p-4">
                  <p className="text-purple-600 text-sm">Pool Commission</p>
                  <p className="text-2xl font-bold text-purple-900">£{stats.revenue.pool_commission_revenue.toFixed(2)}</p>
                </div>
                <div className="bg-green-900/30 rounded-lg p-4 border border-green-500/30">
                  <p className="text-green-400 text-sm">Total Revenue</p>
                  <p className="text-2xl font-bold text-green-400">£{stats.revenue.total_revenue.toFixed(2)}</p>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Users/CRM Tab */}
        {activeTab === "users" && (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* User List */}
            <div className="lg:col-span-1 bg-white rounded-xl border border-purple-200 overflow-hidden">
              <div className="p-4 border-b border-purple-200">
                <form onSubmit={handleSearch} className="flex gap-2">
                  <div className="relative flex-1">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-purple-500" />
                    <Input
                      placeholder="Search users..."
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      className="pl-10 bg-purple-100 border-purple-200 text-purple-900"
                      data-testid="user-search-input"
                    />
                  </div>
                  <Button type="submit" size="sm" className="bg-teal-500 hover:bg-teal-600">
                    Search
                  </Button>
                </form>
              </div>
              <div className="max-h-[600px] overflow-y-auto">
                {users.map(user => (
                  <div
                    key={user.user_id}
                    onClick={() => fetchUserDetail(user.user_id)}
                    className={`p-4 border-b border-purple-200 cursor-pointer transition-colors ${
                      selectedUser === user.user_id ? 'bg-teal-500/20' : 'hover:bg-purple-50'
                    }`}
                    data-testid={`user-row-${user.user_id}`}
                  >
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-full bg-gradient-to-br from-teal-400 to-coral-400 flex items-center justify-center text-purple-900 font-bold">
                        {user.name?.[0] || '?'}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-purple-900 truncate">{user.name}</p>
                        <p className="text-xs text-purple-600 truncate">{user.email}</p>
                      </div>
                      {user.is_premium && (
                        <Crown className="w-4 h-4 text-amber-500" />
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* User Detail */}
            <div className="lg:col-span-2">
              {userDetail ? (
                <div className="bg-white rounded-xl border border-purple-200 p-6 space-y-6">
                  {/* User Header */}
                  <div className="flex items-start gap-4">
                    <div className="w-16 h-16 rounded-full bg-gradient-to-br from-teal-400 to-coral-400 flex items-center justify-center text-purple-900 text-2xl font-bold">
                      {userDetail.user?.name?.[0] || '?'}
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <h2 className="text-xl font-bold text-purple-900">{userDetail.user?.name}</h2>
                        {userDetail.user?.is_premium && (
                          <span className="px-2 py-0.5 bg-amber-500/20 text-amber-400 text-xs rounded-full">
                            Premium
                          </span>
                        )}
                      </div>
                      <p className="text-purple-600">{userDetail.user?.email}</p>
                      <p className="text-sm text-purple-500 mt-1">
                        Joined: {formatDate(userDetail.user?.created_at)}
                      </p>
                    </div>
                  </div>

                  {/* Stats */}
                  <div className="grid grid-cols-4 gap-3">
                    <div className="bg-purple-50 rounded-lg p-3 text-center">
                      <p className="text-2xl font-bold text-purple-900">{userDetail.stats?.matches_count}</p>
                      <p className="text-xs text-purple-600">Matches</p>
                    </div>
                    <div className="bg-purple-50 rounded-lg p-3 text-center">
                      <p className="text-2xl font-bold text-purple-900">{userDetail.stats?.conversations_count}</p>
                      <p className="text-xs text-purple-600">Chats</p>
                    </div>
                    <div className="bg-purple-50 rounded-lg p-3 text-center">
                      <p className="text-2xl font-bold text-purple-900">{userDetail.stats?.playdates_count}</p>
                      <p className="text-xs text-purple-600">Playdates</p>
                    </div>
                    <div className="bg-purple-50 rounded-lg p-3 text-center">
                      <p className="text-2xl font-bold text-purple-900">{userDetail.stats?.pools_count}</p>
                      <p className="text-xs text-purple-600">Pools</p>
                    </div>
                  </div>

                  {/* Profile Info */}
                  {userDetail.profile && (
                    <div className="bg-purple-50 rounded-lg p-4">
                      <h3 className="font-semibold text-purple-900 mb-3">Family Profile</h3>
                      <div className="grid grid-cols-2 gap-4 text-sm">
                        <div>
                          <p className="text-purple-600">Parent</p>
                          <p className="text-purple-900">{userDetail.profile.parent_name}</p>
                        </div>
                        <div>
                          <p className="text-purple-600">Location</p>
                          <p className="text-purple-900">{userDetail.profile.location_city || 'Not set'}</p>
                        </div>
                        <div>
                          <p className="text-purple-600">Children</p>
                          <p className="text-purple-900">{userDetail.profile.children?.length || 0}</p>
                        </div>
                        <div>
                          <p className="text-purple-600">Dietary</p>
                          <p className="text-purple-900">{userDetail.profile.dietary_preferences?.join(', ') || 'None'}</p>
                        </div>
                      </div>
                      {userDetail.profile.children?.length > 0 && (
                        <div className="mt-3 pt-3 border-t border-purple-200">
                          <p className="text-purple-600 text-sm mb-2">Children:</p>
                          <div className="flex flex-wrap gap-2">
                            {userDetail.profile.children.map((child, i) => (
                              <span key={i} className="px-2 py-1 bg-slate-600 rounded text-xs text-purple-900">
                                {child.name}, {child.age}y
                              </span>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  )}

                  {/* Admin Notes */}
                  <div className="bg-purple-50 rounded-lg p-4">
                    <h3 className="font-semibold text-purple-900 mb-3">Admin Notes</h3>
                    <div className="flex gap-2 mb-3">
                      <Input
                        placeholder="Add a note..."
                        value={newNote}
                        onChange={(e) => setNewNote(e.target.value)}
                        className="bg-purple-100 border-purple-200 text-purple-900"
                        data-testid="note-input"
                      />
                      <Button onClick={addNote} size="sm" className="bg-teal-500 hover:bg-teal-600">
                        <Plus className="w-4 h-4" />
                      </Button>
                    </div>
                    <div className="space-y-2 max-h-40 overflow-y-auto">
                      {notes.length === 0 ? (
                        <p className="text-purple-500 text-sm">No notes yet</p>
                      ) : (
                        notes.map(note => (
                          <div key={note.note_id} className="bg-slate-600/50 rounded p-2">
                            <p className="text-purple-900 text-sm">{note.note}</p>
                            <p className="text-xs text-purple-600 mt-1">{formatDate(note.created_at)}</p>
                          </div>
                        ))
                      )}
                    </div>
                  </div>

                  {/* Debug Mode Toggle */}
                  <div className="bg-purple-50 rounded-lg p-4">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <Bug className={`w-5 h-5 ${debugMode ? 'text-green-400' : 'text-purple-600'}`} />
                        <div>
                          <h3 className="font-semibold text-purple-900">Debug Mode</h3>
                          <p className="text-xs text-purple-600">Bypass all premium limits for testing</p>
                        </div>
                      </div>
                      <Button 
                        onClick={toggleDebugMode}
                        className={debugMode 
                          ? 'bg-green-500 hover:bg-green-600' 
                          : 'bg-slate-600 hover:bg-slate-500'
                        }
                        data-testid="debug-mode-toggle"
                      >
                        {debugMode ? 'Enabled' : 'Disabled'}
                      </Button>
                    </div>
                    {debugMode && (
                      <p className="text-green-400 text-xs mt-2">
                        ✓ Unlimited matches • Unlimited messages • Full marketplace access • Playdate planner
                      </p>
                    )}
                  </div>
                </div>
              ) : (
                <div className="bg-white rounded-xl border border-purple-200 p-12 text-center">
                  <Users className="w-12 h-12 text-slate-600 mx-auto mb-3" />
                  <p className="text-purple-600">Select a user to view details</p>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Outreach Tab */}
        {activeTab === "outreach" && outreach && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Incomplete Signups */}
            <div className="bg-white rounded-xl border border-purple-200 overflow-hidden">
              <div className="p-4 border-b border-purple-200 flex items-center justify-between">
                <h3 className="font-semibold text-purple-900 flex items-center gap-2">
                  <AlertCircle className="w-5 h-5 text-amber-500" />
                  Incomplete Signups
                </h3>
                <span className="px-2 py-1 bg-amber-500/20 text-amber-400 text-xs rounded-full">
                  {outreach.total_incomplete}
                </span>
              </div>
              <div className="max-h-96 overflow-y-auto">
                {outreach.incomplete_signups.map(user => (
                  <div key={user.user_id} className="p-4 border-b border-purple-200 hover:bg-purple-50">
                    <p className="font-medium text-purple-900">{user.name}</p>
                    <p className="text-sm text-purple-600">{user.email}</p>
                    <p className="text-xs text-purple-500 mt-1">Signed up: {formatDate(user.created_at)}</p>
                  </div>
                ))}
                {outreach.incomplete_signups.length === 0 && (
                  <p className="p-4 text-purple-500 text-center">All users have completed profiles!</p>
                )}
              </div>
            </div>

            {/* Premium Users */}
            <div className="bg-white rounded-xl border border-purple-200 overflow-hidden">
              <div className="p-4 border-b border-purple-200 flex items-center justify-between">
                <h3 className="font-semibold text-purple-900 flex items-center gap-2">
                  <Crown className="w-5 h-5 text-amber-500" />
                  Premium Users
                </h3>
                <span className="px-2 py-1 bg-amber-500/20 text-amber-400 text-xs rounded-full">
                  {outreach.total_premium}
                </span>
              </div>
              <div className="max-h-96 overflow-y-auto">
                {outreach.premium_users.map(user => (
                  <div key={user.user_id} className="p-4 border-b border-purple-200 hover:bg-purple-50">
                    <p className="font-medium text-purple-900">{user.name}</p>
                    <p className="text-sm text-purple-600">{user.email}</p>
                    <p className="text-xs text-purple-500 mt-1">
                      Premium until: {formatDate(user.premium_until)}
                    </p>
                  </div>
                ))}
                {outreach.premium_users.length === 0 && (
                  <p className="p-4 text-purple-500 text-center">No premium users yet</p>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Newsletter Tab */}
        {activeTab === "newsletter" && (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Compose Newsletter */}
            <div className="lg:col-span-2 bg-white rounded-xl border border-purple-200 p-6">
              <h3 className="font-semibold text-purple-900 mb-4 flex items-center gap-2">
                <Send className="w-5 h-5 text-teal-400" />
                Compose Newsletter
              </h3>
              
              <div className="space-y-4">
                <div>
                  <label className="text-sm text-purple-600 mb-1 block">Subject Line</label>
                  <Input
                    placeholder="e.g., 🎉 New Features This Week!"
                    value={newsletter.subject}
                    onChange={(e) => setNewsletter({...newsletter, subject: e.target.value})}
                    className="bg-purple-100 border-purple-200 text-purple-900"
                    data-testid="newsletter-subject"
                  />
                </div>
                
                <div>
                  <label className="text-sm text-purple-600 mb-1 block">HTML Content</label>
                  <textarea
                    placeholder="<h1>Hello!</h1><p>Your newsletter content here...</p>"
                    value={newsletter.content}
                    onChange={(e) => setNewsletter({...newsletter, content: e.target.value})}
                    className="w-full h-48 p-3 bg-purple-100 border border-purple-200 rounded-lg text-purple-900 resize-none focus:outline-none focus:ring-2 focus:ring-teal-500"
                    data-testid="newsletter-content"
                  />
                </div>
                
                <div>
                  <label className="text-sm text-purple-600 mb-1 block">Audience</label>
                  <select
                    value={newsletter.audience}
                    onChange={(e) => setNewsletter({...newsletter, audience: e.target.value})}
                    className="w-full p-3 bg-purple-100 border border-purple-200 rounded-lg text-purple-900 focus:outline-none focus:ring-2 focus:ring-teal-500"
                    data-testid="newsletter-audience"
                  >
                    <option value="all">All Subscribers</option>
                    <option value="premium">Premium Users Only</option>
                    <option value="new">New Users (Last 7 Days)</option>
                  </select>
                </div>
                
                <div className="flex gap-3">
                  <Button 
                    onClick={sendNewsletter}
                    className="flex-1 bg-teal-500 hover:bg-teal-600"
                    data-testid="send-newsletter-btn"
                  >
                    <Send className="w-4 h-4 mr-2" />
                    Send Newsletter
                  </Button>
                  <Button 
                    onClick={sendTestEmail}
                    variant="outline"
                    className="border-purple-200 text-slate-300 hover:bg-purple-100"
                    data-testid="send-test-email-btn"
                  >
                    <Mail className="w-4 h-4 mr-2" />
                    Test Email
                  </Button>
                </div>
              </div>
            </div>
            
            {/* Subscribers List */}
            <div className="bg-white rounded-xl border border-purple-200 overflow-hidden">
              <div className="p-4 border-b border-purple-200 flex items-center justify-between">
                <h3 className="font-semibold text-purple-900 flex items-center gap-2">
                  <Newspaper className="w-5 h-5 text-teal-400" />
                  Subscribers
                </h3>
                <span className="px-2 py-1 bg-teal-500/20 text-teal-400 text-xs rounded-full">
                  {subscribers.total_active}
                </span>
              </div>
              <div className="max-h-96 overflow-y-auto">
                {subscribers.subscribers?.length > 0 ? (
                  subscribers.subscribers.map(sub => (
                    <div key={sub.subscriber_id} className="p-4 border-b border-purple-200 hover:bg-purple-50">
                      <p className="font-medium text-purple-900">{sub.name || 'Anonymous'}</p>
                      <p className="text-sm text-purple-600">{sub.email}</p>
                      <p className="text-xs text-purple-500 mt-1">Subscribed: {formatDate(sub.subscribed_at)}</p>
                    </div>
                  ))
                ) : (
                  <p className="p-4 text-purple-500 text-center">No subscribers yet</p>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Media Library Tab */}
        {activeTab === "media" && (
          <div className="space-y-6">
            {/* Upload Section */}
            <div className="bg-white rounded-xl border border-purple-200 p-6">
              <h3 className="font-semibold text-purple-900 mb-4 flex items-center gap-2">
                <Upload className="w-5 h-5 text-teal-400" />
                Upload Media
              </h3>
              <p className="text-sm text-purple-600 mb-4">
                Upload images and PDFs for newsletters, landing page, Niamh&apos;s Corner, and more. Once uploaded, copy the URL to use anywhere!
              </p>
              
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                <div>
                  <label className="text-sm text-purple-600 mb-1 block">Category</label>
                  <select
                    value={mediaCategory}
                    onChange={(e) => setMediaCategory(e.target.value)}
                    className="w-full p-3 bg-purple-100 border border-purple-200 rounded-lg text-purple-900"
                    data-testid="media-category-select"
                  >
                    <option value="general">General</option>
                    <option value="newsletter">Newsletter</option>
                    <option value="hero">Hero / Landing</option>
                    <option value="niamh">Niamh&apos;s Corner</option>
                    <option value="venue">Venues</option>
                    <option value="profile">Profile Pictures</option>
                    <option value="menu">Menus / PDFs</option>
                  </select>
                </div>
                <div className="md:col-span-2">
                  <label className="text-sm text-purple-600 mb-1 block">Alt Text (optional)</label>
                  <Input
                    placeholder="Describe the image for accessibility"
                    value={mediaAltText}
                    onChange={(e) => setMediaAltText(e.target.value)}
                    className="bg-purple-100 border-purple-200 text-purple-900"
                    data-testid="media-alt-text"
                  />
                </div>
              </div>
              
              <div className="flex items-center gap-4">
                <label className="flex-1">
                  <input
                    type="file"
                    accept="image/jpeg,image/png,image/gif,image/webp,application/pdf"
                    onChange={handleMediaUpload}
                    className="hidden"
                    data-testid="media-file-input"
                  />
                  <div className="border-2 border-dashed border-purple-300 rounded-xl p-8 text-center cursor-pointer hover:border-teal-400 hover:bg-purple-50 transition-colors">
                    {mediaUploading ? (
                      <div className="flex items-center justify-center gap-2 text-purple-600">
                        <Loader2 className="w-5 h-5 animate-spin" />
                        Uploading...
                      </div>
                    ) : (
                      <>
                        <Upload className="w-8 h-8 text-purple-400 mx-auto mb-2" />
                        <p className="text-purple-600 font-medium">Click to upload or drag and drop</p>
                        <p className="text-xs text-purple-500 mt-1">JPG, PNG, GIF, WebP, PDF (Max 10MB)</p>
                      </>
                    )}
                  </div>
                </label>
              </div>
            </div>

            {/* Media Grid */}
            <div className="bg-white rounded-xl border border-purple-200 p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-semibold text-purple-900 flex items-center gap-2">
                  <Image className="w-5 h-5 text-teal-400" />
                  Your Media ({mediaFiles.length})
                </h3>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => fetchMedia()}
                  className="border-purple-200 text-purple-600"
                >
                  Refresh
                </Button>
              </div>
              
              {mediaFiles.length === 0 ? (
                <div className="text-center py-12 text-purple-500">
                  <Image className="w-12 h-12 mx-auto mb-3 opacity-50" />
                  <p>No media uploaded yet</p>
                  <p className="text-sm">Upload images above to see them here</p>
                </div>
              ) : (
                <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-4">
                  {mediaFiles.map(media => (
                    <div key={media.id} className="group relative bg-purple-50 rounded-lg overflow-hidden border border-purple-200">
                      {media.content_type?.startsWith("image/") ? (
                        <img
                          src={`${BACKEND_URL}/api/media/${media.id}`}
                          alt={media.alt_text || media.original_filename}
                          className="w-full h-32 object-cover"
                        />
                      ) : (
                        <div className="w-full h-32 flex items-center justify-center bg-purple-100">
                          <FileText className="w-8 h-8 text-purple-400" />
                        </div>
                      )}
                      
                      {/* Overlay with actions */}
                      <div className="absolute inset-0 bg-purple-900/80 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2">
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => copyMediaUrl(media.id)}
                          className="text-white hover:bg-white/20"
                          title="Copy URL"
                        >
                          <Copy className="w-4 h-4" />
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => window.open(`${BACKEND_URL}/api/media/${media.id}`, '_blank')}
                          className="text-white hover:bg-white/20"
                          title="View"
                        >
                          <ExternalLink className="w-4 h-4" />
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => deleteMedia(media.id)}
                          className="text-red-300 hover:bg-red-500/20"
                          title="Delete"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                      
                      {/* File info */}
                      <div className="p-2">
                        <p className="text-xs text-purple-900 truncate font-medium">{media.original_filename}</p>
                        <p className="text-xs text-purple-500">{media.category}</p>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Usage Instructions */}
            <div className="bg-gradient-to-r from-purple-100 to-pink-100 rounded-xl border border-purple-200 p-6">
              <h4 className="font-semibold text-purple-900 mb-2">How to use your uploaded media:</h4>
              <ol className="text-sm text-purple-700 space-y-1 list-decimal list-inside">
                <li>Upload an image or PDF above</li>
                <li>Hover over the file and click the <Copy className="w-3 h-3 inline" /> icon to copy its URL</li>
                <li>Paste the URL in your newsletter HTML, landing page code, or anywhere else</li>
                <li>Example: <code className="bg-white/50 px-1 rounded text-xs">&lt;img src=&quot;YOUR_URL&quot; /&gt;</code></li>
              </ol>
            </div>
          </div>
        )}

        {/* Funding & Grants Tab */}
        {activeTab === "funding" && (
          <div className="space-y-6">
            {/* Summary Cards */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="bg-white rounded-xl p-4 border border-purple-200">
                <div className="w-10 h-10 rounded-lg bg-blue-500/20 flex items-center justify-center mb-3">
                  <Landmark className="w-5 h-5 text-blue-400" />
                </div>
                <p className="text-2xl font-bold text-purple-900">{grants.grants?.length || 0}</p>
                <p className="text-sm text-purple-600">Grants Tracked</p>
              </div>
              <div className="bg-white rounded-xl p-4 border border-purple-200">
                <div className="w-10 h-10 rounded-lg bg-amber-500/20 flex items-center justify-center mb-3">
                  <FileText className="w-5 h-5 text-amber-400" />
                </div>
                <p className="text-2xl font-bold text-purple-900">
                  {grants.grants?.filter(g => g.status === 'submitted').length || 0}
                </p>
                <p className="text-sm text-purple-600">Applications Pending</p>
              </div>
              <div className="bg-white rounded-xl p-4 border border-purple-200">
                <div className="w-10 h-10 rounded-lg bg-green-500/20 flex items-center justify-center mb-3">
                  <DollarSign className="w-5 h-5 text-green-400" />
                </div>
                <p className="text-2xl font-bold text-purple-900">
                  {grants.grants?.filter(g => g.status === 'approved').length || 0}
                </p>
                <p className="text-sm text-purple-600">Approved</p>
              </div>
              <div className="bg-white rounded-xl p-4 border border-purple-200">
                <div className="w-10 h-10 rounded-lg bg-purple-500/20 flex items-center justify-center mb-3">
                  <Target className="w-5 h-5 text-purple-400" />
                </div>
                <p className="text-2xl font-bold text-purple-900">{goals.length}</p>
                <p className="text-sm text-purple-600">Community Goals</p>
              </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              {/* Active Grants Tracker */}
              <div className="lg:col-span-2 bg-white rounded-xl border border-purple-200 overflow-hidden">
                <div className="p-4 border-b border-purple-200 flex items-center justify-between">
                  <h3 className="font-semibold text-purple-900 flex items-center gap-2">
                    <Landmark className="w-5 h-5 text-blue-400" />
                    Grants Tracker
                  </h3>
                  <Button 
                    onClick={() => setShowAddGrant(true)}
                    size="sm" 
                    className="bg-teal-500 hover:bg-teal-600"
                  >
                    <Plus className="w-4 h-4 mr-1" />
                    Add Grant
                  </Button>
                </div>
                <div className="max-h-[500px] overflow-y-auto">
                  {grants.grants?.length > 0 ? (
                    grants.grants.map(grant => (
                      <div key={grant.grant_id} className="p-4 border-b border-purple-200 hover:bg-purple-50">
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-1">
                              <h4 className="font-medium text-purple-900">{grant.name}</h4>
                              <span className={`text-xs px-2 py-0.5 rounded-full ${STATUS_COLORS[grant.status]}`}>
                                {grant.status.replace('_', ' ')}
                              </span>
                              {grant.priority === 'high' && (
                                <span className="text-xs bg-red-500/20 text-red-400 px-2 py-0.5 rounded-full">
                                  High Priority
                                </span>
                              )}
                            </div>
                            <p className="text-sm text-purple-600">{grant.funder}</p>
                            {grant.typical_amount && (
                              <p className="text-xs text-teal-400 mt-1">{grant.typical_amount}</p>
                            )}
                            {grant.deadline && (
                              <p className="text-xs text-amber-400 mt-1">Deadline: {formatDate(grant.deadline)}</p>
                            )}
                          </div>
                          <div className="flex items-center gap-2">
                            <select
                              value={grant.status}
                              onChange={(e) => updateGrantStatus(grant.grant_id, e.target.value)}
                              className="text-xs bg-purple-100 border border-purple-200 rounded px-2 py-1 text-purple-900"
                            >
                              <option value="researching">Researching</option>
                              <option value="preparing">Preparing</option>
                              <option value="submitted">Submitted</option>
                              <option value="approved">Approved</option>
                              <option value="rejected">Rejected</option>
                              <option value="not_applicable">N/A</option>
                            </select>
                            {grant.url && (
                              <a href={grant.url} target="_blank" rel="noopener noreferrer" className="p-1 hover:bg-slate-600 rounded">
                                <ExternalLink className="w-4 h-4 text-purple-600" />
                              </a>
                            )}
                            <button onClick={() => deleteGrant(grant.grant_id)} className="p-1 hover:bg-red-500/20 rounded">
                              <Trash2 className="w-4 h-4 text-red-400" />
                            </button>
                          </div>
                        </div>
                        {grant.notes && (
                          <p className="text-xs text-purple-500 mt-2 bg-purple-50 p-2 rounded">{grant.notes}</p>
                        )}
                      </div>
                    ))
                  ) : (
                    <div className="p-8 text-center">
                      <Landmark className="w-12 h-12 text-slate-600 mx-auto mb-3" />
                      <p className="text-purple-600">No grants tracked yet</p>
                      <p className="text-purple-500 text-sm mt-1">Add from the suggested list or create your own</p>
                    </div>
                  )}
                </div>
              </div>

              {/* Community Goals & Suggested Grants */}
              <div className="space-y-6">
                {/* Community Goals */}
                <div className="bg-white rounded-xl border border-purple-200 overflow-hidden">
                  <div className="p-4 border-b border-purple-200">
                    <h3 className="font-semibold text-purple-900 flex items-center gap-2">
                      <Target className="w-5 h-5 text-purple-400" />
                      Community Goals
                    </h3>
                    <p className="text-xs text-purple-600 mt-1">Align with funding criteria</p>
                  </div>
                  <div className="max-h-64 overflow-y-auto">
                    {goals.map(goal => (
                      <div key={goal.goal_id} className="p-3 border-b border-purple-200">
                        <div className="flex items-center justify-between mb-1">
                          <span className="text-xs bg-purple-500/20 text-purple-400 px-2 py-0.5 rounded-full">
                            {goal.category}
                          </span>
                          <span className="text-xs text-purple-600">
                            {goal.current}/{goal.target}
                          </span>
                        </div>
                        <p className="text-sm text-purple-900 font-medium">{goal.title}</p>
                        <div className="w-full bg-purple-100 rounded-full h-1.5 mt-2">
                          <div 
                            className="bg-purple-500 h-1.5 rounded-full" 
                            style={{ width: `${Math.min((goal.current / goal.target) * 100, 100)}%` }}
                          />
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Suggested Grants */}
                <div className="bg-white rounded-xl border border-purple-200 overflow-hidden">
                  <div className="p-4 border-b border-purple-200">
                    <h3 className="font-semibold text-purple-900 flex items-center gap-2">
                      <Landmark className="w-5 h-5 text-green-400" />
                      Suggested UK Grants
                    </h3>
                  </div>
                  <div className="max-h-64 overflow-y-auto">
                    {seedGrants.slice(0, 6).map((grant, i) => (
                      <div key={i} className="p-3 border-b border-purple-200 hover:bg-purple-50">
                        <p className="text-sm text-purple-900 font-medium">{grant.name}</p>
                        <p className="text-xs text-purple-600">{grant.funder}</p>
                        <button 
                          onClick={() => addFromSeed(grant)}
                          className="text-xs text-teal-400 hover:text-teal-300 mt-1"
                        >
                          + Add to tracker
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>

            {/* Monthly History */}
            {grants.monthly_history?.length > 0 && (
              <div className="bg-white rounded-xl border border-purple-200 p-4">
                <h3 className="font-semibold text-purple-900 mb-4 flex items-center gap-2">
                  <Calendar className="w-5 h-5 text-cyan-400" />
                  Monthly Progress History
                </h3>
                <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3">
                  {grants.monthly_history.slice(0, 6).map(month => (
                    <div key={month.month} className="bg-purple-50 rounded-lg p-3">
                      <p className="text-xs text-purple-600 mb-1">{month.month}</p>
                      <p className="text-lg font-bold text-purple-900">{month.total_grants} grants</p>
                      <div className="flex gap-1 mt-2 text-xs">
                        <span className="text-green-400">{month.by_status?.approved || 0} ✓</span>
                        <span className="text-amber-400">{month.by_status?.submitted || 0} ⏳</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Add Grant Modal */}
            {showAddGrant && (
              <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
                <div className="bg-white rounded-xl w-full max-w-md p-6">
                  <h3 className="text-lg font-bold text-purple-900 mb-4">Add Grant to Tracker</h3>
                  <div className="space-y-4">
                    <div>
                      <label className="text-sm text-purple-600 block mb-1">Grant Name *</label>
                      <Input
                        value={newGrant.name}
                        onChange={(e) => setNewGrant({...newGrant, name: e.target.value})}
                        className="bg-purple-100 border-purple-200 text-purple-900"
                        placeholder="e.g., National Lottery Community Fund"
                      />
                    </div>
                    <div>
                      <label className="text-sm text-purple-600 block mb-1">Funder</label>
                      <Input
                        value={newGrant.funder}
                        onChange={(e) => setNewGrant({...newGrant, funder: e.target.value})}
                        className="bg-purple-100 border-purple-200 text-purple-900"
                        placeholder="e.g., National Lottery"
                      />
                    </div>
                    <div>
                      <label className="text-sm text-purple-600 block mb-1">Typical Amount</label>
                      <Input
                        value={newGrant.typical_amount}
                        onChange={(e) => setNewGrant({...newGrant, typical_amount: e.target.value})}
                        className="bg-purple-100 border-purple-200 text-purple-900"
                        placeholder="e.g., £10,000 - £50,000"
                      />
                    </div>
                    <div>
                      <label className="text-sm text-purple-600 block mb-1">URL</label>
                      <Input
                        value={newGrant.url}
                        onChange={(e) => setNewGrant({...newGrant, url: e.target.value})}
                        className="bg-purple-100 border-purple-200 text-purple-900"
                        placeholder="https://..."
                      />
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <label className="text-sm text-purple-600 block mb-1">Status</label>
                        <select
                          value={newGrant.status}
                          onChange={(e) => setNewGrant({...newGrant, status: e.target.value})}
                          className="w-full p-2 bg-purple-100 border border-purple-200 rounded-lg text-purple-900"
                        >
                          <option value="researching">Researching</option>
                          <option value="preparing">Preparing</option>
                          <option value="submitted">Submitted</option>
                        </select>
                      </div>
                      <div>
                        <label className="text-sm text-purple-600 block mb-1">Priority</label>
                        <select
                          value={newGrant.priority}
                          onChange={(e) => setNewGrant({...newGrant, priority: e.target.value})}
                          className="w-full p-2 bg-purple-100 border border-purple-200 rounded-lg text-purple-900"
                        >
                          <option value="low">Low</option>
                          <option value="medium">Medium</option>
                          <option value="high">High</option>
                        </select>
                      </div>
                    </div>
                    <div className="flex gap-3 pt-2">
                      <Button onClick={() => setShowAddGrant(false)} variant="outline" className="flex-1 border-purple-200 text-slate-300">
                        Cancel
                      </Button>
                      <Button onClick={addGrant} className="flex-1 bg-teal-500 hover:bg-teal-600">
                        Add Grant
                      </Button>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Content Tab */}
        {activeTab === "content" && (
          <AdminContentManager adminToken={adminToken} />
        )}

        {/* Research Tab */}
        {activeTab === "research" && (
          <div className="space-y-6">
            <h2 className="text-2xl font-bold text-purple-900">Business Research & Insights</h2>
            
            {/* Insurance Research */}
            <div className="bg-white rounded-xl p-6 border border-purple-100 shadow-sm">
              <h3 className="text-lg font-bold text-purple-900 mb-4 flex items-center gap-2">
                <Shield className="w-5 h-5 text-purple-500" />
                Insurance & Cover Requirements
              </h3>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-purple-100">
                      <th className="text-left py-2 text-purple-700">Insurance Type</th>
                      <th className="text-left py-2 text-purple-700">Required?</th>
                      <th className="text-left py-2 text-purple-700">Why You Need It</th>
                      <th className="text-left py-2 text-purple-700">Typical Cost</th>
                    </tr>
                  </thead>
                  <tbody className="text-gray-700">
                    <tr className="border-b border-purple-50">
                      <td className="py-3 font-medium">Employers Liability</td>
                      <td className="py-3"><span className="bg-green-100 text-green-700 px-2 py-1 rounded">YES (if hiring)</span></td>
                      <td className="py-3">Legally mandatory - £2,500/day fine if not compliant</td>
                      <td className="py-3">£5-10m cover</td>
                    </tr>
                    <tr className="border-b border-purple-50">
                      <td className="py-3 font-medium">Public Liability</td>
                      <td className="py-3"><span className="bg-amber-100 text-amber-700 px-2 py-1 rounded">Recommended</span></td>
                      <td className="py-3">Covers injury/damage claims from users during playdates</td>
                      <td className="py-3">£1-5m cover</td>
                    </tr>
                    <tr className="border-b border-purple-50">
                      <td className="py-3 font-medium">Professional Indemnity</td>
                      <td className="py-3"><span className="bg-amber-100 text-amber-700 px-2 py-1 rounded">Recommended</span></td>
                      <td className="py-3">Covers negligence claims (faulty vetting, software bugs)</td>
                      <td className="py-3">£1-5m cover</td>
                    </tr>
                    <tr>
                      <td className="py-3 font-medium">Cyber Insurance</td>
                      <td className="py-3"><span className="bg-red-100 text-red-700 px-2 py-1 rounded">Critical</span></td>
                      <td className="py-3">You handle children data - covers breaches, GDPR fines</td>
                      <td className="py-3">£100k-500k</td>
                    </tr>
                  </tbody>
                </table>
              </div>
              <p className="text-sm text-purple-600 mt-4">
                Providers to contact: <strong>Hiscox</strong>, <strong>Simply Business</strong>, <strong>PolicyBee</strong>
              </p>
            </div>

            {/* Competitor Pricing */}
            <div className="bg-white rounded-xl p-6 border border-purple-100 shadow-sm">
              <h3 className="text-lg font-bold text-purple-900 mb-4 flex items-center gap-2">
                <TrendingUp className="w-5 h-5 text-purple-500" />
                Competitor Pricing Analysis
              </h3>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-purple-100">
                      <th className="text-left py-2 text-purple-700">App</th>
                      <th className="text-left py-2 text-purple-700">Model</th>
                      <th className="text-left py-2 text-purple-700">Price</th>
                      <th className="text-left py-2 text-purple-700">Notes</th>
                    </tr>
                  </thead>
                  <tbody className="text-gray-700">
                    <tr className="border-b border-purple-50">
                      <td className="py-3 font-medium">Mush</td>
                      <td className="py-3">Freemium</td>
                      <td className="py-3">~£4.20/mo</td>
                      <td className="py-3">Expert advice, premium features</td>
                    </tr>
                    <tr className="border-b border-purple-50">
                      <td className="py-3 font-medium">Peanut</td>
                      <td className="py-3">Freemium</td>
                      <td className="py-3">~£5-8/mo</td>
                      <td className="py-3">Enhanced matching, read receipts</td>
                    </tr>
                    <tr className="border-b border-purple-50">
                      <td className="py-3 font-medium">Hoop</td>
                      <td className="py-3">Free</td>
                      <td className="py-3">£0</td>
                      <td className="py-3">Monetizes via activity providers</td>
                    </tr>
                    <tr className="border-b border-purple-50">
                      <td className="py-3 font-medium">Bubble (Sitters)</td>
                      <td className="py-3">Transaction fee</td>
                      <td className="py-3">20% per booking</td>
                      <td className="py-3">Takes cut from sitters</td>
                    </tr>
                    <tr>
                      <td className="py-3 font-medium text-purple-600">KiddiPlay</td>
                      <td className="py-3">Freemium</td>
                      <td className="py-3">£6.99/mo or £59.99/yr</td>
                      <td className="py-3 text-purple-600 font-medium">More features = premium pricing justified</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>

            {/* Revenue Streams */}
            <div className="bg-white rounded-xl p-6 border border-purple-100 shadow-sm">
              <h3 className="text-lg font-bold text-purple-900 mb-4 flex items-center gap-2">
                <DollarSign className="w-5 h-5 text-green-500" />
                Revenue Streams
              </h3>
              <div className="grid md:grid-cols-2 gap-4">
                <div className="bg-purple-50 rounded-lg p-4">
                  <h4 className="font-bold text-purple-900">Recurring Revenue</h4>
                  <ul className="mt-2 space-y-2 text-sm text-purple-800">
                    <li>• Premium subscriptions (£6.99/mo)</li>
                    <li>• Annual subscriptions (£59.99/yr)</li>
                    <li>• Partner listing fees (£49-99/mo)</li>
                  </ul>
                </div>
                <div className="bg-pink-50 rounded-lg p-4">
                  <h4 className="font-bold text-pink-900">Passive Revenue</h4>
                  <ul className="mt-2 space-y-2 text-sm text-pink-800">
                    <li>• Affiliate commissions (5-10%)</li>
                    <li>• Booking commissions (Phase 2)</li>
                    <li>• Sponsored content</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Projections Tab */}
        {activeTab === "projections" && (
          <div className="space-y-6">
            <h2 className="text-2xl font-bold text-purple-900">Cash Flow Projections</h2>
            
            {/* Summary Cards */}
            <div className="grid md:grid-cols-4 gap-4">
              <div className="bg-gradient-to-br from-green-100 to-teal-100 rounded-xl p-6 border border-green-200">
                <p className="text-sm text-green-700">Year 1 Target</p>
                <p className="text-3xl font-bold text-green-800">£50,000</p>
              </div>
              <div className="bg-gradient-to-br from-purple-100 to-pink-100 rounded-xl p-6 border border-purple-200">
                <p className="text-sm text-purple-700">Target Subscribers</p>
                <p className="text-3xl font-bold text-purple-800">500</p>
              </div>
              <div className="bg-gradient-to-br from-amber-100 to-orange-100 rounded-xl p-6 border border-amber-200">
                <p className="text-sm text-amber-700">Partner Venues</p>
                <p className="text-3xl font-bold text-amber-800">50</p>
              </div>
              <div className="bg-gradient-to-br from-blue-100 to-cyan-100 rounded-xl p-6 border border-blue-200">
                <p className="text-sm text-blue-700">Break-even</p>
                <p className="text-3xl font-bold text-blue-800">Month 6</p>
              </div>
            </div>

            {/* Monthly Projection Table */}
            <div className="bg-white rounded-xl p-6 border border-purple-100 shadow-sm">
              <h3 className="text-lg font-bold text-purple-900 mb-4">12-Month Cash Flow Forecast</h3>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b-2 border-purple-200 bg-purple-50">
                      <th className="text-left py-3 px-2 text-purple-700">Month</th>
                      <th className="text-right py-3 px-2 text-purple-700">Users</th>
                      <th className="text-right py-3 px-2 text-purple-700">Premium</th>
                      <th className="text-right py-3 px-2 text-purple-700">Sub Revenue</th>
                      <th className="text-right py-3 px-2 text-purple-700">Partners</th>
                      <th className="text-right py-3 px-2 text-purple-700">Partner Rev</th>
                      <th className="text-right py-3 px-2 text-purple-700">Affiliate</th>
                      <th className="text-right py-3 px-2 text-purple-700 font-bold">Total</th>
                    </tr>
                  </thead>
                  <tbody className="text-gray-700">
                    {[
                      { m: "Jul 2026", users: 100, prem: 10, sub: 70, part: 2, partRev: 98, aff: 20 },
                      { m: "Aug 2026", users: 200, prem: 25, sub: 175, part: 5, partRev: 245, aff: 50 },
                      { m: "Sep 2026", users: 350, prem: 45, sub: 315, part: 8, partRev: 392, aff: 100 },
                      { m: "Oct 2026", users: 500, prem: 70, sub: 490, part: 12, partRev: 588, aff: 150 },
                      { m: "Nov 2026", users: 700, prem: 100, sub: 700, part: 18, partRev: 882, aff: 200 },
                      { m: "Dec 2026", users: 1000, prem: 150, sub: 1050, part: 25, partRev: 1225, aff: 300 },
                      { m: "Jan 2027", users: 1300, prem: 200, sub: 1400, part: 30, partRev: 1470, aff: 400 },
                      { m: "Feb 2027", users: 1600, prem: 260, sub: 1820, part: 35, partRev: 1715, aff: 500 },
                      { m: "Mar 2027", users: 2000, prem: 330, sub: 2310, part: 40, partRev: 1960, aff: 600 },
                      { m: "Apr 2027", users: 2500, prem: 420, sub: 2940, part: 45, partRev: 2205, aff: 750 },
                      { m: "May 2027", users: 3000, prem: 500, sub: 3500, part: 50, partRev: 2450, aff: 900 },
                      { m: "Jun 2027", users: 3500, prem: 600, sub: 4200, part: 55, partRev: 2695, aff: 1100 },
                    ].map((row, idx) => (
                      <tr key={idx} className={`border-b border-purple-50 ${idx === 11 ? 'bg-green-50 font-medium' : ''}`}>
                        <td className="py-2 px-2">{row.m}</td>
                        <td className="py-2 px-2 text-right">{row.users.toLocaleString()}</td>
                        <td className="py-2 px-2 text-right">{row.prem}</td>
                        <td className="py-2 px-2 text-right">£{row.sub.toLocaleString()}</td>
                        <td className="py-2 px-2 text-right">{row.part}</td>
                        <td className="py-2 px-2 text-right">£{row.partRev.toLocaleString()}</td>
                        <td className="py-2 px-2 text-right">£{row.aff}</td>
                        <td className="py-2 px-2 text-right font-bold text-purple-700">£{(row.sub + row.partRev + row.aff).toLocaleString()}</td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot>
                    <tr className="bg-purple-100 font-bold">
                      <td className="py-3 px-2">Year 1 Total</td>
                      <td className="py-3 px-2 text-right">-</td>
                      <td className="py-3 px-2 text-right">-</td>
                      <td className="py-3 px-2 text-right">£18,970</td>
                      <td className="py-3 px-2 text-right">-</td>
                      <td className="py-3 px-2 text-right">£15,925</td>
                      <td className="py-3 px-2 text-right">£5,070</td>
                      <td className="py-3 px-2 text-right text-purple-800">£39,965</td>
                    </tr>
                  </tfoot>
                </table>
              </div>
              <p className="text-xs text-purple-600 mt-4">
                * Assumptions: 15% conversion to premium, £6.99/mo avg, 5% churn, £49/mo partner fees, 5% affiliate commission on £1000 avg bookings
              </p>
            </div>

            {/* Key Metrics */}
            <div className="grid md:grid-cols-3 gap-4">
              <div className="bg-white rounded-xl p-6 border border-purple-100 shadow-sm">
                <h4 className="font-bold text-purple-900 mb-3">Subscription Metrics</h4>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-gray-600">Monthly ARPU</span>
                    <span className="font-medium text-purple-700">£6.99</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Annual ARPU</span>
                    <span className="font-medium text-purple-700">£59.99</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Target Conversion</span>
                    <span className="font-medium text-purple-700">15%</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Est. Churn Rate</span>
                    <span className="font-medium text-purple-700">5%/mo</span>
                  </div>
                </div>
              </div>
              <div className="bg-white rounded-xl p-6 border border-purple-100 shadow-sm">
                <h4 className="font-bold text-purple-900 mb-3">Partner Metrics</h4>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-gray-600">Featured Partner</span>
                    <span className="font-medium text-purple-700">£49/mo</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Premium Partner</span>
                    <span className="font-medium text-purple-700">£99/mo</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Target Partners Y1</span>
                    <span className="font-medium text-purple-700">50</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Avg. Partner Fee</span>
                    <span className="font-medium text-purple-700">£49/mo</span>
                  </div>
                </div>
              </div>
              <div className="bg-white rounded-xl p-6 border border-purple-100 shadow-sm">
                <h4 className="font-bold text-purple-900 mb-3">Affiliate Metrics</h4>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-gray-600">Avg. Commission</span>
                    <span className="font-medium text-purple-700">5-8%</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Avg. Booking Value</span>
                    <span className="font-medium text-purple-700">£50</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Click-to-Book Rate</span>
                    <span className="font-medium text-purple-700">~10%</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Est. Y1 Affiliate</span>
                    <span className="font-medium text-purple-700">£5,000+</span>
                  </div>
                </div>
              </div>
            </div>

            {/* BACS Invoice Creation */}
            <div className="bg-white rounded-xl p-6 border border-purple-100 shadow-sm">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-bold text-purple-900 flex items-center gap-2">
                  <Receipt className="w-5 h-5 text-teal-500" />
                  Create BACS Invoice
                </h3>
              </div>
              
              <div className="grid md:grid-cols-2 gap-4 mb-4">
                <div>
                  <label className="text-sm text-purple-600 mb-1 block">Partner/Client Name *</label>
                  <Input
                    placeholder="e.g., Soft Play Paradise Ltd"
                    value={newInvoice.partner_name}
                    onChange={(e) => setNewInvoice({...newInvoice, partner_name: e.target.value})}
                    className="bg-purple-50 border-purple-200"
                  />
                </div>
                <div>
                  <label className="text-sm text-purple-600 mb-1 block">Email Address *</label>
                  <Input
                    type="email"
                    placeholder="accounts@example.com"
                    value={newInvoice.partner_email}
                    onChange={(e) => setNewInvoice({...newInvoice, partner_email: e.target.value})}
                    className="bg-purple-50 border-purple-200"
                  />
                </div>
                <div className="md:col-span-2">
                  <label className="text-sm text-purple-600 mb-1 block">Description *</label>
                  <Input
                    placeholder="e.g., Partner Listing Fee - July 2026"
                    value={newInvoice.description}
                    onChange={(e) => setNewInvoice({...newInvoice, description: e.target.value})}
                    className="bg-purple-50 border-purple-200"
                  />
                </div>
                <div>
                  <label className="text-sm text-purple-600 mb-1 block">Amount (£) *</label>
                  <Input
                    type="number"
                    placeholder="49.00"
                    value={newInvoice.amount_pounds}
                    onChange={(e) => setNewInvoice({...newInvoice, amount_pounds: e.target.value})}
                    className="bg-purple-50 border-purple-200"
                  />
                </div>
                <div>
                  <label className="text-sm text-purple-600 mb-1 block">Due In (Days)</label>
                  <select
                    value={newInvoice.due_days}
                    onChange={(e) => setNewInvoice({...newInvoice, due_days: parseInt(e.target.value)})}
                    className="w-full p-2 bg-purple-50 border border-purple-200 rounded-lg"
                  >
                    <option value={7}>7 days</option>
                    <option value={14}>14 days</option>
                    <option value={30}>30 days</option>
                    <option value={60}>60 days</option>
                  </select>
                </div>
                <div className="md:col-span-2">
                  <label className="text-sm text-purple-600 mb-1 block">Notes (Optional)</label>
                  <Input
                    placeholder="Any additional notes..."
                    value={newInvoice.notes}
                    onChange={(e) => setNewInvoice({...newInvoice, notes: e.target.value})}
                    className="bg-purple-50 border-purple-200"
                  />
                </div>
              </div>
              
              <div className="flex gap-3">
                <Button
                  onClick={async () => {
                    if (!newInvoice.partner_name || !newInvoice.partner_email || !newInvoice.amount_pounds) {
                      toast.error("Please fill in all required fields");
                      return;
                    }
                    try {
                      const res = await axios.post(`${API}/admin/invoices/create`, newInvoice, {
                        headers: { Authorization: `Bearer ${adminToken}` }
                      });
                      toast.success(`Invoice ${res.data.invoice_id} created!`);
                      setNewInvoice({ partner_name: "", partner_email: "", description: "", amount_pounds: "", due_days: 30, notes: "" });
                    } catch (error) {
                      toast.error(error.response?.data?.detail || "Failed to create invoice");
                    }
                  }}
                  className="bg-gradient-to-r from-purple-500 to-pink-500 text-white"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Create Invoice
                </Button>
                <Button
                  variant="outline"
                  onClick={async () => {
                    if (!newInvoice.partner_name || !newInvoice.partner_email || !newInvoice.amount_pounds) {
                      toast.error("Please fill in all required fields");
                      return;
                    }
                    try {
                      const res = await axios.post(`${API}/admin/invoices/create`, newInvoice, {
                        headers: { Authorization: `Bearer ${adminToken}` }
                      });
                      // Then send it
                      await axios.post(`${API}/admin/invoices/${res.data.invoice_id}/send`, {}, {
                        headers: { Authorization: `Bearer ${adminToken}` }
                      });
                      toast.success(`Invoice created and sent to ${newInvoice.partner_email}!`);
                      setNewInvoice({ partner_name: "", partner_email: "", description: "", amount_pounds: "", due_days: 30, notes: "" });
                    } catch (error) {
                      toast.error(error.response?.data?.detail || "Failed to create/send invoice");
                    }
                  }}
                  className="border-teal-300 text-teal-700 hover:bg-teal-50"
                >
                  <Send className="w-4 h-4 mr-2" />
                  Create & Send
                </Button>
              </div>
              
              {/* Bank Details Reference */}
              <div className="mt-4 p-4 bg-green-50 rounded-lg border border-green-200">
                <p className="text-sm font-medium text-green-800 mb-2">Your BACS Bank Details (included in invoice emails):</p>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-xs text-green-700">
                  <div><span className="font-medium">Bank:</span> ANNA</div>
                  <div><span className="font-medium">Account:</span> KiddiPlay Ltd</div>
                  <div><span className="font-medium">Sort Code:</span> 04-03-70</div>
                  <div><span className="font-medium">Acc No:</span> 23301728</div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

// Stat Card Component
const StatCard = ({ icon: Icon, label, value, color }) => {
  const colorClasses = {
    teal: 'bg-teal-100 text-teal-600',
    amber: 'bg-amber-100 text-amber-600',
    pink: 'bg-pink-100 text-pink-600',
    blue: 'bg-blue-100 text-blue-600',
    purple: 'bg-purple-100 text-purple-600',
    coral: 'bg-orange-100 text-orange-600',
    green: 'bg-green-100 text-green-600',
    cyan: 'bg-cyan-100 text-cyan-600',
  };

  return (
    <div className="bg-white rounded-xl p-4 border border-purple-100 shadow-sm">
      <div className={`w-10 h-10 rounded-lg ${colorClasses[color]} flex items-center justify-center mb-3`}>
        <Icon className="w-5 h-5" />
      </div>
      <p className="text-2xl font-bold text-purple-900">{value}</p>
      <p className="text-sm text-purple-600">{label}</p>
    </div>
  );
};

export default AdminDashboard;
